import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import { Table, TableRef } from '../../../components/Table/Table';
import { useAppDispatch } from '../../../redux/hooks';
import {
  fetchCompanyEmployees,
  uploadCompanyEmployeesCsv,
  deleteCompanyEmployee,
  restoreCompanyEmployee,
  activateCompanyEmployee,
  inactivateCompanyEmployee,
} from '../../../redux/modules/b2bThriftPlan/employees/companyEmployee.actions';
import {
  companyEmployeeSelector,
  companyEmployeesLoadingSelector,
  companyEmployeeUploadCsvLoadingSelector,
  companyEmployeeDeleteLoadingSelector,
  companyEmployeeRestoreLoadingSelector,
  companyEmployeeActivateLoadingSelector,
  companyEmployeeInactivateLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/employees/companyEmployee.selectors';
import { AsyncDialog, AsyncDialogRef } from '../../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialog, ConfirmationDialogData } from '../../../components/ConfirmationDialog';
import { round2Format } from '../../../utils/number';
import { locations } from '../../../routes/locations';
import { downloadCsvTemplateByName } from '../../../utils/csvTemplates';

interface CompanyEmployeesProps {
  showHeader?: boolean;
  showUploadButton?: boolean;
  showCreateButton?: boolean;
  onEdit?: (employeeId: number) => void;
  onDelete?: (employeeId: number) => void;
}

export const CompanyEmployees = ({
  showHeader = true,
  showUploadButton = true,
  showCreateButton = true,
  onEdit,
  onDelete,
}: CompanyEmployeesProps = {}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const { companyEmployees, totalCompanyEmployees } = useSelector(companyEmployeeSelector);
  const isLoading = useSelector(companyEmployeesLoadingSelector);
  const isUploadLoading = useSelector(companyEmployeeUploadCsvLoadingSelector);
  const isDeleteLoading = useSelector(companyEmployeeDeleteLoadingSelector);
  const isRestoreLoading = useSelector(companyEmployeeRestoreLoadingSelector);
  const isActivateLoading = useSelector(companyEmployeeActivateLoadingSelector);
  const isInactivateLoading = useSelector(companyEmployeeInactivateLoadingSelector);
  const tableRef = useRef<TableRef>(null);
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloadTemplateLoading, setIsDownloadTemplateLoading] = useState(false);

  const onAddNew = (): void => {
    if (companyId) {
      navigate(locations.companyEmployee(Number(companyId), 'create'));
    }
  };

  const onUploadCsv = (): void => {
    setUploadDialogOpen(true);
  };

  const onDownloadCsvTemplate = async (): Promise<void> => {
    try {
      setIsDownloadTemplateLoading(true);
      await downloadCsvTemplateByName('company_group_employees');
    } catch (error: any) {
      const message = error?.message || 'Failed to download CSV template.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsDownloadTemplateLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !companyId) {
      enqueueSnackbar('Please select a CSV file', { variant: 'warning' });
      return;
    }

    try {
      await dispatch(uploadCompanyEmployeesCsv({ companyId: Number(companyId), file: selectedFile })).unwrap();
      enqueueSnackbar('CSV file uploaded successfully!', { variant: 'success' });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Refresh the company employees list
      dispatch(fetchCompanyEmployees({ companyId: Number(companyId) }));
    } catch (err: any) {
      enqueueSnackbar(`Upload failed: ${err?.message || 'Unknown error'}`, { variant: 'error' });
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onEditEmployee = (employeeId: number) => {
    if (onEdit) {
      onEdit(employeeId);
    } else if (companyId) {
      navigate(locations.companyEmployee(Number(companyId), 'edit', employeeId));
    }
  };

  const onDeleteEmployee = async (employeeId: number) => {
    if (!companyId) return;

    const employee = companyEmployees.find((emp) => emp.id === employeeId);
    const employeeName = employee?.full_name_en || employee?.email || `Employee #${employeeId}`;

    const performDelete = async () => {
      if (onDelete) {
        // If custom delete handler is provided, use it
        await onDelete(employeeId);
      } else {
        // Otherwise, use the default delete action
        await dispatch(deleteCompanyEmployee({ companyId: Number(companyId), employeeId })).unwrap();
        enqueueSnackbar('Company employee deleted successfully!', { variant: 'success' });
      }
      tableRef.current?.reload();
    };

    await asyncDialogRef.current?.show({
      title: 'Delete Company Employee',
      description: `Are you sure you want to delete ${employeeName}?`,
      onConfirm: async () => {
        try {
          await performDelete();
          // Dialog will close automatically after onConfirm resolves
        } catch (err: any) {
          enqueueSnackbar(`Delete failed: ${err?.message || 'Unknown error'}`, { variant: 'error' });
          // Re-throw to prevent dialog from closing
          throw err;
        }
      },
    });
  };

  const onRestoreEmployee = async (employeeId: number) => {
    if (!companyId) return;

    try {
      await dispatch(restoreCompanyEmployee({ companyId: Number(companyId), employeeId })).unwrap();
      enqueueSnackbar('Company employee restored successfully!', { variant: 'success' });
      tableRef.current?.reload();
    } catch (err: any) {
      enqueueSnackbar(`Restore failed: ${err?.message || 'Unknown error'}`, { variant: 'error' });
    }
  };

  const onActivateEmployee = async (employeeId: number) => {
    if (!companyId) return;

    try {
      await dispatch(activateCompanyEmployee({ companyId: Number(companyId), employeeId })).unwrap();
      enqueueSnackbar('Company employee activated successfully!', { variant: 'success' });
      tableRef.current?.reload();
    } catch (err: any) {
      enqueueSnackbar(`Activate failed: ${err?.message || 'Unknown error'}`, { variant: 'error' });
    }
  };

  const onInactivateEmployee = async (employeeId: number) => {
    if (!companyId) return;

    try {
      await dispatch(inactivateCompanyEmployee({ companyId: Number(companyId), employeeId })).unwrap();
      enqueueSnackbar('Company employee inactivated successfully!', { variant: 'success' });
      tableRef.current?.reload();
    } catch (err: any) {
      enqueueSnackbar(`Inactivate failed: ${err?.message || 'Unknown error'}`, { variant: 'error' });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      width: 150,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];
        const isDeleted = params.row.is_deleted;
        const employeeStatus = params.row.employeeStatus?.status?.toLowerCase() || '';
        const isActive = employeeStatus === 'active';
        const isInactive = employeeStatus !== 'active';

        if (onEdit || showCreateButton) {
          actions.push(
            <GridActionsCellItem
              key={`edit-company-employee-${params.row.id}`}
              icon={<EditOutlinedIcon />}
              label="Edit"
              onClick={() => onEditEmployee(params.row.id)}
              disabled={isDeleted}
            />,
          );
        }
        if (onDelete || showCreateButton) {
          actions.push(
            <GridActionsCellItem
              key={`delete-company-employee-${params.row.id}`}
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => onDeleteEmployee(params.row.id)}
              disabled={isDeleteLoading || isDeleted}
            />,
          );
        }
        if (onDelete || showCreateButton) {
          actions.push(
            <GridActionsCellItem
              key={`restore-company-employee-${params.row.id}`}
              icon={<RestoreIcon />}
              label="Restore"
              onClick={() => onRestoreEmployee(params.row.id)}
              disabled={!isDeleted || isRestoreLoading}
            />,
          );
        }
        // Activate/Inactivate actions in three dots menu - always show both, disable based on status or if deleted
        actions.push(
          <GridActionsCellItem
            key={`activate-company-employee-${params.row.id}`}
            icon={<CheckCircleIcon />}
            label="Activate"
            onClick={() => onActivateEmployee(params.row.id)}
            disabled={isDeleted || !isInactive || isActivateLoading}
            showInMenu
          />,
        );
        actions.push(
          <GridActionsCellItem
            key={`inactivate-company-employee-${params.row.id}`}
            icon={<CancelIcon />}
            label="Inactive"
            onClick={() => onInactivateEmployee(params.row.id)}
            disabled={isDeleted || !isActive || isInactivateLoading}
            showInMenu
          />,
        );
        return actions;
      },
    },
    {
      field: 'id',
      headerName: 'Id',
      width: 70,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'is_deleted',
      headerName: 'Is Deleted',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const isDeleted = params.row.is_deleted;
        return (
          <Chip
            label={isDeleted ? 'Deleted' : 'Active'}
            sx={{
              backgroundColor: isDeleted ? '#ff4444' : '#4caf50',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        );
      },
    },
    {
      field: 'employeeStatus',
      headerName: 'Employee Status',
      width: 150,
      valueGetter: (params) => params.row.employeeStatus?.status || '',
      renderCell: (params: GridRenderCellParams) => {
        const status = params.row.employeeStatus?.status?.toLowerCase() || '';
        const getStatusColor = () => {
          switch (status) {
            case 'active':
              return '#4caf50'; // Green
            case 'inactive':
              return '#ff4444'; // Red
            case 'pending':
              return '#ff9800'; // Orange
            case 'suspended':
              return '#9e9e9e'; // Grey
            default:
              return '#757575'; // Default grey
          }
        };
        return (
          <Chip
            label={params.row.employeeStatus?.status || 'Unknown'}
            sx={{
              backgroundColor: getStatusColor(),
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        );
      },
    },
    {
      field: 'full_name_en',
      headerName: 'Full Name (EN)',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'full_name_ar',
      headerName: 'Full Name (AR)',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'phone_number',
      headerName: 'Phone Number',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'national_id',
      headerName: 'National ID',
      width: 130,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'national_id_expiry',
      headerName: 'National ID Expiry',
      width: 150,
      type: 'date',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'gender',
      headerName: 'Gender',
      width: 100,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'date_of_birth',
      headerName: 'Date of Birth',
      width: 130,
      type: 'date',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'companyGroup',
      headerName: 'Company Group',
      width: 150,
      valueGetter: (params) => params.row.companyGroup?.name || '',
      valueFormatter: (params) => params.value,
    },
    {
      field: 'department',
      headerName: 'Department',
      width: 150,
      valueGetter: (params) => params.row.department?.name || '',
      valueFormatter: (params) => params.value,
    },
    {
      field: 'job_title',
      headerName: 'Job Title',
      width: 150,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'joining_date',
      headerName: 'Joining Date',
      width: 130,
      type: 'date',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'salary',
      headerName: 'Salary',
      width: 120,
      valueFormatter: (params) => round2Format(Number(params.value)),
    },
    {
      field: 'employee_contribution_amount',
      headerName: 'Contribution Amount',
      width: 150,
      valueFormatter: (params) => round2Format(Number(params.value)),
    },
    {
      field: 'maritalStatus',
      headerName: 'Marital Status',
      width: 130,
      valueGetter: (params) => params.row.maritalStatus?.status || '',
      valueFormatter: (params) => params.value,
    },
    {
      field: 'address',
      headerName: 'Address',
      width: 200,
      valueFormatter: (params) => params.value,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
  ];

  const tableContent = (
    <>
      <Table
        ref={tableRef}
        columns={columns}
        rows={companyEmployees}
        rowCount={totalCompanyEmployees}
        fetchItems={fetchCompanyEmployees}
        fetchItemsCustomParams={{ companyId: Number(companyId) }}
        loading={isLoading}
        initialSortModel={[{ field: 'id', sort: 'desc' }]}
        toolbarExportProps={{
          csvOptions: { fileName: `company-employees-${Date.now()}` },
          excelOptions: { fileName: `company-employees-${Date.now()}` },
        }}
      />
      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
    </>
  );

  if (!showHeader) {
    return tableContent;
  }

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Company Employees &nbsp;
          {showCreateButton && (
            <Fab color="primary" size="small" aria-label="Create Employee" onClick={onAddNew} sx={{ mr: 1 }}>
              <AddIcon />
            </Fab>
          )}
          {showUploadButton && (
            <>
              <Fab
                color="secondary"
                size="small"
                aria-label="Download CSV Template"
                onClick={onDownloadCsvTemplate}
                sx={{ mr: 1 }}
                disabled={isDownloadTemplateLoading}
              >
                {isDownloadTemplateLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <SimCardDownloadOutlinedIcon sx={{ color: 'common.white' }} />
                )}
              </Fab>
              <Fab color="secondary" size="small" aria-label="Upload CSV" onClick={onUploadCsv}>
                <UploadFileIcon />
              </Fab>
            </>
          )}
        </Typography>
      </Box>

      {/* Upload CSV Dialog */}
      {showUploadButton && (
        <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} fullWidth maxWidth="sm">
          <DialogTitle>Upload Company Employees CSV</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                component="label"
                aria-label="Select CSV file"
                color="primary"
                startIcon={<UploadFileIcon />}
                fullWidth
              >
                {selectedFile ? selectedFile.name : 'Select CSV File'}
                <input ref={fileInputRef} hidden accept=".csv,text/csv" type="file" onChange={handleFileChange} />
              </Button>
              {selectedFile && (
                <Typography variant="body2" color="text.secondary">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUploadDialog} color="secondary">
              Cancel
            </Button>
            <LoadingButton
              onClick={handleUpload}
              variant="contained"
              color="primary"
              loading={isUploadLoading}
              disabled={!selectedFile}
            >
              Upload
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {tableContent}
    </Container>
  );
};
